/**
 * @fileoverview Connect a matter's dated triggers to persisted rule-derived deadlines and explicit recomputation. Backend ruleset/provider coverage owns calculation meaning; this workspace owns selected triggers and review actions. `kc-internal-backend/services/court_rules/__init__.py` maps the area. A successful calculation does not establish complete legal-rule coverage.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppSelect, AppSpinner, AppTextInput } from '../shared';
import './CourtRulesPanel.css';

const STATUS_TONE = {
  computed: 'info',
  accepted: 'success',
  dismissed: 'neutral',
  stale: 'warning',
};

function Pill({ tone = 'neutral', children }) {
  return <span className={`court-rules__pill court-rules__pill--${tone}`}>{children}</span>;
}

function formatDate(value) {
  if (!value) return '—';
  return value;
}



export default function CourtRulesPanel({ matterNumber, active = true }) {
  const toast = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [triggerKey, setTriggerKey] = useState('');
  const [triggerDate, setTriggerDate] = useState('');

  const showToast = useCallback((detail, severity = 'info') => {
    if (typeof toast === 'function') toast({ severity, summary: 'Court rules', detail, life: 4000 });
  }, [toast]);

  const load = useCallback(async () => {
    if (!matterNumber) return;
    setLoading(true);
    try {
      const response = await api.get(`/matter/${encodeURIComponent(matterNumber)}/court-rules`);
      setSnapshot(response.data);
      const triggers = response.data?.ruleset?.triggers || [];
      setTriggerKey((current) => current || (triggers[0]?.key || ''));
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not load court rules.', 'error');
    } finally {
      setLoading(false);
    }
  }, [matterNumber, showToast]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const triggerOptions = useMemo(() => {
    const fromRuleset = (snapshot?.ruleset?.triggers || []).map((t) => ({
      value: t.key,
      label: t.label || t.key,
    }));
    if (fromRuleset.length) return fromRuleset;
    return [{ value: 'service_of_citation', label: 'Service of citation / petition' }];
  }, [snapshot]);

  const saveTrigger = async () => {
    if (!triggerKey || !triggerDate) {
      showToast('Choose a trigger and date.', 'warn');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/matter/${encodeURIComponent(matterNumber)}/court-rules/triggers`, {
        trigger_key: triggerKey,
        trigger_date: triggerDate,
      });
      showToast('Trigger saved; dependent deadlines recalculated.', 'success');
      await load();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not save trigger.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setSaving(true);
    try {
      await api.post(`/matter/${encodeURIComponent(matterNumber)}/court-rules/recompute`, {});
      showToast('Deadlines recomputed from current triggers.', 'success');
      await load();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not recompute.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (deadlineId, status) => {
    try {
      await api.patch(
        `/matter/${encodeURIComponent(matterNumber)}/court-rules/deadlines/${deadlineId}`,
        { status },
      );
      await load();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not update deadline.', 'error');
    }
  };

  const removeTrigger = async (triggerId) => {
    try {
      await api.delete(
        `/matter/${encodeURIComponent(matterNumber)}/court-rules/triggers/${triggerId}`,
      );
      showToast('Trigger removed; dependent deadlines marked stale.', 'success');
      await load();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Could not remove trigger.', 'error');
    }
  };

  const mapping = snapshot?.jurisdiction_mapping;
  const ruleset = snapshot?.ruleset;

  return (
    <div className="court-rules" data-testid="court-rules-panel">
      <section className="court-rules__section" aria-label="Court rules overview">
        <header className="court-rules__header">
          <div>
            <h3 className="court-rules__title">Court rules deadlines</h3>
            <p className="court-rules__subtitle">
              Trigger dates drive dependent deadlines for this matter&apos;s jurisdiction.
              Same review flow as AI Deadlines: compute, then accept or dismiss.
            </p>
          </div>
          <div className="court-rules__header-actions">
            <AppButton
              label={saving ? 'Working…' : 'Recompute'}
              icon="pi pi-refresh"
              variant="secondary"
              onClick={recompute}
              disabled={saving || loading}
            />
          </div>
        </header>

        <p className="court-rules__meta">
          Jurisdiction: <strong>{snapshot?.jurisdiction || '—'}</strong>
          {mapping?.ruleset_label ? (
            <>
              {' '}→ ruleset <strong>{mapping.ruleset_label}</strong>
              {mapping.is_stub ? ' (stub)' : ''}
            </>
          ) : (
            ' → no ruleset mapped yet (set Jurisdiction on the matter, e.g. Texas)'
          )}
        </p>

        {(ruleset?.is_stub || ruleset?.description) && (
          <p className="court-rules__disclaimer" role="note">
            {ruleset.description ||
              'Stub ruleset for demos only. License LawToolBox / CalendarRules (or similar) for production completeness.'}
          </p>
        )}
      </section>

      <section className="court-rules__section" aria-label="Set a trigger date">
        <header className="court-rules__header">
          <div>
            <h3 className="court-rules__title">Trigger dates</h3>
            <p className="court-rules__subtitle">
              Changing a trigger recalculates every deadline that depends on it.
            </p>
          </div>
        </header>

        <div className="court-rules__form">
          <div>
            <span className="court-rules__label">Trigger</span>
            <AppSelect
              value={triggerKey}
              options={triggerOptions}
              onChange={setTriggerKey}
              placeholder="Select trigger"
            />
          </div>
          <div>
            <span className="court-rules__label">Date</span>
            <AppTextInput
              type="date"
              value={triggerDate}
              onChange={(e) => setTriggerDate(e.target?.value || '')}
            />
          </div>
          <AppButton
            label={saving ? 'Saving…' : 'Save & recalculate'}
            icon="pi pi-calendar"
            onClick={saveTrigger}
            disabled={saving || loading || !triggerKey || !triggerDate}
          />
        </div>

        {loading && !snapshot && (
          <div className="court-rules__loading"><AppSpinner /></div>
        )}

        {!loading && !(snapshot?.triggers || []).length && (
          <p className="court-rules__empty">No trigger dates yet. Add service, discovery, or judgment dates above.</p>
        )}

        {(snapshot?.triggers || []).length > 0 && (
          <ul className="court-rules__list">
            {snapshot.triggers.map((trigger) => (
              <li key={trigger.id} className="court-rules__row">
                <div>
                  <div className="court-rules__name">{trigger.label || trigger.trigger_key}</div>
                  <div className="court-rules__note">{trigger.trigger_key}</div>
                </div>
                <div>{formatDate(trigger.trigger_date)}</div>
                <div className="court-rules__note">{trigger.source || 'manual'}</div>
                <AppButton
                  label="Remove"
                  icon="pi pi-trash"
                  variant="secondary"
                  onClick={() => removeTrigger(trigger.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="court-rules__section" aria-label="Computed deadlines">
        <header className="court-rules__header">
          <div>
            <h3 className="court-rules__title">Computed deadlines</h3>
            <p className="court-rules__subtitle">
              Accept deadlines you will track; dismiss ones that do not apply.
            </p>
          </div>
        </header>

        {!(snapshot?.deadlines || []).length ? (
          <p className="court-rules__empty">No computed deadlines yet. Save a trigger to generate them.</p>
        ) : (
          <ul className="court-rules__list">
            {snapshot.deadlines.map((deadline) => (
              <li key={deadline.id} className="court-rules__row">
                <div>
                  <div className="court-rules__name">{deadline.label}</div>
                  <div className="court-rules__note">
                    {deadline.rule_citation}
                    {deadline.is_stub ? ' · stub example' : ''}
                  </div>
                  {deadline.computation_notes && (
                    <div className="court-rules__note">{deadline.computation_notes}</div>
                  )}
                </div>
                <div>
                  <div className="court-rules__name">{formatDate(deadline.deadline_date)}</div>
                  <div className="court-rules__note">
                    from {deadline.trigger_key} {formatDate(deadline.trigger_date)}
                  </div>
                </div>
                <div>
                  <Pill tone={STATUS_TONE[deadline.status] || 'neutral'}>{deadline.status}</Pill>
                </div>
                <div className="court-rules__header-actions">
                  {deadline.status !== 'accepted' && (
                    <AppButton
                      label="Accept"
                      icon="pi pi-check"
                      onClick={() => setStatus(deadline.id, 'accepted')}
                    />
                  )}
                  {deadline.status !== 'dismissed' && (
                    <AppButton
                      label="Dismiss"
                      icon="pi pi-times"
                      variant="secondary"
                      onClick={() => setStatus(deadline.id, 'dismissed')}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
